/**
 * 项目克隆分析器
 * 
 * 深度分析参考项目，用于创建风格一致的新项目
 * 
 * 设计理念：
 * 1. 深度理解源项目架构，而非简单的文件复制
 * 2. 区分业务代码和基础设施代码
 * 3. 提取可复用的工具类、样式、配置等
 * 4. 生成详细的分析报告和开发计划
 * 
 * @module core/projectCloneAnalyzer
 */

import * as fs from 'fs';
import * as path from 'path';
import glob from 'fast-glob';
import { Logger, ConsoleLogger } from './types.js';

/**
 * 架构层级定义
 */
export interface ArchitectureLayer {
    /** 层级名称 */
    name: string;
    /** 层级描述 */
    description: string;
    /** 对应的目录路径 */
    directories: string[];
    /** 关键文件列表 */
    keyFiles: string[];
    /** 是否可复用（非业务代码） */
    reusable: boolean;
    /** 优先级（1-10，数字越小越核心） */
    priority: number;
}

/**
 * 工具类分析结果
 */
export interface UtilityAnalysis {
    /** 文件路径 */
    path: string;
    /** 工具类型 */
    type: 'http' | 'storage' | 'validation' | 'format' | 'auth' | 'router' | 'i18n' | 'other';
    /** 导出的函数/类 */
    exports: string[];
    /** 依赖的外部库 */
    dependencies: string[];
    /** 使用频率（被引用次数） */
    usageCount: number;
    /** 是否推荐复用 */
    recommended: boolean;
    /** 复用建议 */
    suggestion?: string;
}

/**
 * 样式系统分析结果
 */
export interface StyleSystemAnalysis {
    /** 全局样式文件 */
    globalStyles: string[];
    /** CSS 预处理器 */
    preprocessor: 'scss' | 'sass' | 'less' | 'stylus' | 'css' | null;
    /** 使用的 CSS 框架 */
    cssFrameworks: string[];
    /** UI 组件库 */
    uiLibrary: string | null;
    /** UI 组件库二次封装 */
    uiOverrides: {
        path: string;
        components: string[];
    }[];
    /** 主题/变量文件 */
    themeFiles: string[];
    /** CSS 变量定义 */
    cssVariables: {
        file: string;
        variables: string[];
    }[];
    /** 公共 mixins */
    mixins: string[];
}

/**
 * 配置系统分析结果
 */
export interface ConfigSystemAnalysis {
    /** 环境配置 */
    envFiles: string[];
    /** 构建配置 */
    buildConfig: {
        tool: 'vite' | 'webpack' | 'rollup' | 'other';
        configFile: string;
        keyFeatures: string[];
    } | null;
    /** 路由配置 */
    routerConfig: {
        type: 'vue-router' | 'react-router' | 'go_router' | 'other';
        configFile: string;
        features: string[];
    } | null;
    /** 状态管理配置 */
    stateConfig: {
        type: 'pinia' | 'vuex' | 'redux' | 'mobx' | 'bloc' | 'riverpod' | 'other';
        configFile: string;
    } | null;
    /** HTTP 客户端配置 */
    httpConfig: {
        client: 'axios' | 'fetch' | 'dio' | 'other';
        configFile: string;
        features: string[];
    } | null;
    /** 国际化配置 */
    i18nConfig: {
        type: 'vue-i18n' | 'i18next' | 'flutter_localizations' | 'other';
        configFile: string;
        locales: string[];
    } | null;
    /** 其他重要配置 */
    otherConfigs: {
        name: string;
        file: string;
        description: string;
    }[];
}

/**
 * 核心页面/组件分析
 */
export interface CorePagesAnalysis {
    /** 登录页面 */
    loginPage: string | null;
    /** 主布局 */
    mainLayout: string | null;
    /** 侧边栏/导航 */
    navigation: string | null;
    /** 错误页面（404、403等） */
    errorPages: string[];
    /** 其他核心页面 */
    otherCorePages: {
        name: string;
        path: string;
        description: string;
    }[];
}

/**
 * 项目骨架定义
 */
export interface ProjectSkeleton {
    /** 必须保留的目录结构 */
    requiredDirectories: {
        path: string;
        description: string;
        files?: string[];
    }[];
    /** 核心工具类 */
    coreUtils: UtilityAnalysis[];
    /** 样式系统 */
    styleSystem: StyleSystemAnalysis;
    /** 配置系统 */
    configSystem: ConfigSystemAnalysis;
    /** 核心页面 */
    corePages: CorePagesAnalysis;
    /** 推荐删除的业务文件 */
    businessFilesToRemove: string[];
    /** 需要清理的文件（保留结构，清空内容） */
    filesToClean: string[];
}

/**
 * 深度分析结果
 */
export interface DeepAnalysisResult {
    /** 分析是否成功 */
    success: boolean;
    /** 项目名称 */
    projectName: string;
    /** 项目路径 */
    projectPath: string;
    /** 分析时间 */
    analyzedAt: string;
    /** 技术栈摘要 */
    techStack: {
        framework: string;
        language: string;
        buildTool: string;
        uiLibrary: string | null;
        stateManagement: string | null;
    };
    /** 架构层级 */
    architectureLayers: ArchitectureLayer[];
    /** 工具类分析 */
    utilities: UtilityAnalysis[];
    /** 样式系统 */
    styleSystem: StyleSystemAnalysis;
    /** 配置系统 */
    configSystem: ConfigSystemAnalysis;
    /** 核心页面 */
    corePages: CorePagesAnalysis;
    /** 项目骨架（可复用部分） */
    skeleton: ProjectSkeleton;
    /** 质量评估 */
    qualityAssessment: {
        score: number;
        strengths: string[];
        weaknesses: string[];
        suggestions: string[];
    };
    /** MCP 模板建议 */
    templateSuggestions: {
        templateId: string;
        reason: string;
        replaceFiles: string[];
    }[];
}

/**
 * 开发计划
 */
export interface DevelopmentPlan {
    /** 计划标题 */
    title: string;
    /** 参考项目 */
    referenceProject: string;
    /** 新项目名称 */
    newProjectName: string;
    /** 生成时间 */
    generatedAt: string;
    /** 阶段列表 */
    phases: {
        phase: number;
        name: string;
        description: string;
        tasks: {
            task: string;
            priority: 'high' | 'medium' | 'low';
            estimatedTime: string;
            files?: string[];
        }[];
    }[];
    /** 注意事项 */
    notes: string[];
}

/**
 * 项目克隆分析器
 */
export class ProjectCloneAnalyzer {
    private logger: Logger;

    constructor(logger?: Logger) {
        this.logger = logger || new ConsoleLogger();
    }

    /**
     * 深度分析项目
     */
    async analyzeProject(projectPath: string): Promise<DeepAnalysisResult> {
        this.logger.log(`🔍 开始深度分析项目: ${projectPath}`);

        // 验证路径
        if (!fs.existsSync(projectPath)) {
            throw new Error(`项目路径不存在: ${projectPath}`);
        }

        const projectName = path.basename(projectPath);

        // 检测项目类型
        const projectType = await this.detectProjectType(projectPath);
        this.logger.log(`📦 检测到项目类型: ${projectType}`);

        // 根据项目类型分析
        let result: DeepAnalysisResult;
        
        switch (projectType) {
            case 'vue':
                result = await this.analyzeVueProject(projectPath, projectName);
                break;
            case 'react':
                result = await this.analyzeReactProject(projectPath, projectName);
                break;
            case 'flutter':
                result = await this.analyzeFlutterProject(projectPath, projectName);
                break;
            default:
                result = await this.analyzeGenericProject(projectPath, projectName);
        }

        // 生成质量评估
        result.qualityAssessment = this.assessQuality(result);

        // 生成 MCP 模板建议
        result.templateSuggestions = this.suggestTemplates(result);

        this.logger.log(`✅ 项目分析完成`);
        return result;
    }

    /**
     * 检测项目类型
     */
    private async detectProjectType(projectPath: string): Promise<'vue' | 'react' | 'flutter' | 'generic'> {
        // Flutter 项目
        if (fs.existsSync(path.join(projectPath, 'pubspec.yaml'))) {
            return 'flutter';
        }

        // Web 项目
        const packageJsonPath = path.join(projectPath, 'package.json');
        if (fs.existsSync(packageJsonPath)) {
            try {
                const pkg = JSON.parse(fs.readFileSync(packageJsonPath, 'utf-8'));
                const deps = { ...pkg.dependencies, ...pkg.devDependencies };
                
                if (deps['vue']) return 'vue';
                if (deps['react']) return 'react';
            } catch {
                // 忽略解析错误
            }
        }

        // 通过文件后缀检测
        const vueFiles = await glob('**/*.vue', { cwd: projectPath, ignore: ['node_modules/**'] });
        if (vueFiles.length > 0) return 'vue';

        const tsxFiles = await glob('**/*.tsx', { cwd: projectPath, ignore: ['node_modules/**'] });
        if (tsxFiles.length > 0) return 'react';

        return 'generic';
    }

    /**
     * 分析 Vue 项目
     */
    private async analyzeVueProject(projectPath: string, projectName: string): Promise<DeepAnalysisResult> {
        const result: DeepAnalysisResult = {
            success: true,
            projectName,
            projectPath,
            analyzedAt: new Date().toISOString(),
            techStack: {
                framework: 'Vue 3',
                language: 'TypeScript',
                buildTool: 'Vite',
                uiLibrary: null,
                stateManagement: null
            },
            architectureLayers: [],
            utilities: [],
            styleSystem: await this.analyzeStyleSystem(projectPath, 'vue'),
            configSystem: await this.analyzeConfigSystem(projectPath, 'vue'),
            corePages: await this.analyzeCorePages(projectPath, 'vue'),
            skeleton: {
                requiredDirectories: [],
                coreUtils: [],
                styleSystem: await this.analyzeStyleSystem(projectPath, 'vue'),
                configSystem: await this.analyzeConfigSystem(projectPath, 'vue'),
                corePages: await this.analyzeCorePages(projectPath, 'vue'),
                businessFilesToRemove: [],
                filesToClean: []
            },
            qualityAssessment: {
                score: 0,
                strengths: [],
                weaknesses: [],
                suggestions: []
            },
            templateSuggestions: []
        };

        // 分析 package.json
        await this.analyzePackageJson(projectPath, result);

        // 分析架构层级
        result.architectureLayers = await this.analyzeVueArchitecture(projectPath);

        // 分析工具类
        result.utilities = await this.analyzeUtilities(projectPath, 'vue');

        // 生成骨架
        result.skeleton = this.generateSkeleton(result);

        return result;
    }

    /**
     * 分析 React 项目
     */
    private async analyzeReactProject(projectPath: string, projectName: string): Promise<DeepAnalysisResult> {
        // 类似 Vue 的分析逻辑
        const result = await this.analyzeVueProject(projectPath, projectName);
        result.techStack.framework = 'React';
        return result;
    }

    /**
     * 分析 Flutter 项目
     */
    private async analyzeFlutterProject(projectPath: string, projectName: string): Promise<DeepAnalysisResult> {
        const result: DeepAnalysisResult = {
            success: true,
            projectName,
            projectPath,
            analyzedAt: new Date().toISOString(),
            techStack: {
                framework: 'Flutter',
                language: 'Dart',
                buildTool: 'Flutter SDK',
                uiLibrary: 'Material Design',
                stateManagement: null
            },
            architectureLayers: [],
            utilities: [],
            styleSystem: await this.analyzeStyleSystem(projectPath, 'flutter'),
            configSystem: await this.analyzeConfigSystem(projectPath, 'flutter'),
            corePages: await this.analyzeCorePages(projectPath, 'flutter'),
            skeleton: {
                requiredDirectories: [],
                coreUtils: [],
                styleSystem: await this.analyzeStyleSystem(projectPath, 'flutter'),
                configSystem: await this.analyzeConfigSystem(projectPath, 'flutter'),
                corePages: await this.analyzeCorePages(projectPath, 'flutter'),
                businessFilesToRemove: [],
                filesToClean: []
            },
            qualityAssessment: {
                score: 0,
                strengths: [],
                weaknesses: [],
                suggestions: []
            },
            templateSuggestions: []
        };

        // 分析 pubspec.yaml
        await this.analyzePubspec(projectPath, result);

        // 分析架构层级
        result.architectureLayers = await this.analyzeFlutterArchitecture(projectPath);

        // 分析工具类
        result.utilities = await this.analyzeUtilities(projectPath, 'flutter');

        // 生成骨架
        result.skeleton = this.generateSkeleton(result);

        return result;
    }

    /**
     * 分析通用项目
     */
    private async analyzeGenericProject(projectPath: string, projectName: string): Promise<DeepAnalysisResult> {
        const result: DeepAnalysisResult = {
            success: true,
            projectName,
            projectPath,
            analyzedAt: new Date().toISOString(),
            techStack: {
                framework: 'Unknown',
                language: 'Unknown',
                buildTool: 'Unknown',
                uiLibrary: null,
                stateManagement: null
            },
            architectureLayers: [],
            utilities: [],
            styleSystem: await this.analyzeStyleSystem(projectPath, 'generic'),
            configSystem: await this.analyzeConfigSystem(projectPath, 'generic'),
            corePages: await this.analyzeCorePages(projectPath, 'generic'),
            skeleton: {
                requiredDirectories: [],
                coreUtils: [],
                styleSystem: await this.analyzeStyleSystem(projectPath, 'generic'),
                configSystem: await this.analyzeConfigSystem(projectPath, 'generic'),
                corePages: await this.analyzeCorePages(projectPath, 'generic'),
                businessFilesToRemove: [],
                filesToClean: []
            },
            qualityAssessment: {
                score: 0,
                strengths: [],
                weaknesses: [],
                suggestions: []
            },
            templateSuggestions: []
        };

        return result;
    }

    /**
     * 分析 package.json
     */
    private async analyzePackageJson(projectPath: string, result: DeepAnalysisResult): Promise<void> {
        const packageJsonPath = path.join(projectPath, 'package.json');
        if (!fs.existsSync(packageJsonPath)) return;

        try {
            const pkg = JSON.parse(fs.readFileSync(packageJsonPath, 'utf-8'));
            const deps = { ...pkg.dependencies, ...pkg.devDependencies };

            // 构建工具
            if (deps['vite']) result.techStack.buildTool = 'Vite';
            else if (deps['webpack']) result.techStack.buildTool = 'Webpack';

            // UI 库
            if (deps['element-plus']) result.techStack.uiLibrary = 'Element Plus';
            else if (deps['ant-design-vue']) result.techStack.uiLibrary = 'Ant Design Vue';
            else if (deps['naive-ui']) result.techStack.uiLibrary = 'Naive UI';

            // 状态管理
            if (deps['pinia']) result.techStack.stateManagement = 'Pinia';
            else if (deps['vuex']) result.techStack.stateManagement = 'Vuex';

            // 语言
            if (deps['typescript']) result.techStack.language = 'TypeScript';
            else result.techStack.language = 'JavaScript';

        } catch (error) {
            this.logger.error(`解析 package.json 失败: ${error}`);
        }
    }

    /**
     * 分析 pubspec.yaml
     */
    private async analyzePubspec(projectPath: string, result: DeepAnalysisResult): Promise<void> {
        const pubspecPath = path.join(projectPath, 'pubspec.yaml');
        if (!fs.existsSync(pubspecPath)) return;

        try {
            const content = fs.readFileSync(pubspecPath, 'utf-8');

            // 状态管理
            if (content.includes('flutter_bloc:') || content.includes('bloc:')) {
                result.techStack.stateManagement = 'BLoC';
            } else if (content.includes('provider:')) {
                result.techStack.stateManagement = 'Provider';
            } else if (content.includes('riverpod:')) {
                result.techStack.stateManagement = 'Riverpod';
            }

        } catch (error) {
            this.logger.error(`解析 pubspec.yaml 失败: ${error}`);
        }
    }

    /**
     * 分析 Vue 项目架构
     */
    private async analyzeVueArchitecture(projectPath: string): Promise<ArchitectureLayer[]> {
        const layers: ArchitectureLayer[] = [];
        const srcPath = path.join(projectPath, 'src');
        
        if (!fs.existsSync(srcPath)) {
            return layers;
        }

        // 检测常见的架构目录
        const directoryChecks = [
            { dir: 'api', name: 'API 层', desc: 'HTTP 请求封装', reusable: true, priority: 2 },
            { dir: 'utils', name: '工具层', desc: '通用工具函数', reusable: true, priority: 3 },
            { dir: 'composables', name: 'Composables', desc: 'Vue 组合式函数', reusable: true, priority: 4 },
            { dir: 'hooks', name: 'Hooks', desc: 'Vue 组合式函数', reusable: true, priority: 4 },
            { dir: 'stores', name: '状态管理', desc: 'Pinia/Vuex 状态', reusable: false, priority: 5 },
            { dir: 'store', name: '状态管理', desc: 'Pinia/Vuex 状态', reusable: false, priority: 5 },
            { dir: 'router', name: '路由层', desc: '路由配置', reusable: true, priority: 3 },
            { dir: 'components', name: '组件层', desc: 'Vue 组件', reusable: false, priority: 6 },
            { dir: 'views', name: '视图层', desc: '页面组件', reusable: false, priority: 7 },
            { dir: 'pages', name: '页面层', desc: '页面组件', reusable: false, priority: 7 },
            { dir: 'assets', name: '资源层', desc: '静态资源', reusable: true, priority: 8 },
            { dir: 'styles', name: '样式层', desc: '全局样式', reusable: true, priority: 2 },
            { dir: 'locales', name: '国际化', desc: '多语言配置', reusable: true, priority: 4 },
            { dir: 'i18n', name: '国际化', desc: '多语言配置', reusable: true, priority: 4 },
            { dir: 'config', name: '配置层', desc: '应用配置', reusable: true, priority: 2 },
            { dir: 'directives', name: '指令层', desc: 'Vue 自定义指令', reusable: true, priority: 5 },
            { dir: 'plugins', name: '插件层', desc: 'Vue 插件', reusable: true, priority: 4 },
            { dir: 'types', name: '类型定义', desc: 'TypeScript 类型', reusable: true, priority: 3 },
        ];

        for (const check of directoryChecks) {
            const dirPath = path.join(srcPath, check.dir);
            if (fs.existsSync(dirPath)) {
                const files = await glob('**/*', { cwd: dirPath, onlyFiles: true });
                layers.push({
                    name: check.name,
                    description: check.desc,
                    directories: [check.dir],
                    keyFiles: files.slice(0, 10), // 只取前10个文件作为示例
                    reusable: check.reusable,
                    priority: check.priority
                });
            }
        }

        // 按优先级排序
        layers.sort((a, b) => a.priority - b.priority);

        return layers;
    }

    /**
     * 分析 Flutter 项目架构
     */
    private async analyzeFlutterArchitecture(projectPath: string): Promise<ArchitectureLayer[]> {
        const layers: ArchitectureLayer[] = [];
        const libPath = path.join(projectPath, 'lib');
        
        if (!fs.existsSync(libPath)) {
            return layers;
        }

        // Flutter 常见架构目录
        const directoryChecks = [
            { dir: 'core', name: '核心层', desc: '核心功能', reusable: true, priority: 1 },
            { dir: 'data', name: '数据层', desc: '数据源和仓库', reusable: false, priority: 3 },
            { dir: 'domain', name: '领域层', desc: '业务逻辑', reusable: false, priority: 4 },
            { dir: 'presentation', name: '表示层', desc: 'UI 组件', reusable: false, priority: 5 },
            { dir: 'features', name: '功能模块', desc: '功能模块', reusable: false, priority: 6 },
            { dir: 'shared', name: '共享层', desc: '共享组件', reusable: true, priority: 2 },
            { dir: 'widgets', name: '组件层', desc: 'UI 组件', reusable: true, priority: 4 },
            { dir: 'utils', name: '工具层', desc: '工具函数', reusable: true, priority: 2 },
            { dir: 'services', name: '服务层', desc: '业务服务', reusable: true, priority: 3 },
            { dir: 'models', name: '模型层', desc: '数据模型', reusable: false, priority: 4 },
            { dir: 'config', name: '配置层', desc: '应用配置', reusable: true, priority: 2 },
            { dir: 'theme', name: '主题层', desc: '主题样式', reusable: true, priority: 2 },
            { dir: 'l10n', name: '国际化', desc: '多语言', reusable: true, priority: 3 },
        ];

        for (const check of directoryChecks) {
            const dirPath = path.join(libPath, check.dir);
            if (fs.existsSync(dirPath)) {
                const files = await glob('**/*.dart', { cwd: dirPath });
                layers.push({
                    name: check.name,
                    description: check.desc,
                    directories: [check.dir],
                    keyFiles: files.slice(0, 10),
                    reusable: check.reusable,
                    priority: check.priority
                });
            }
        }

        layers.sort((a, b) => a.priority - b.priority);
        return layers;
    }

    /**
     * 分析工具类
     */
    private async analyzeUtilities(projectPath: string, type: 'vue' | 'react' | 'flutter' | 'generic'): Promise<UtilityAnalysis[]> {
        const utilities: UtilityAnalysis[] = [];
        
        // 根据项目类型确定搜索路径
        const basePath = type === 'flutter' 
            ? path.join(projectPath, 'lib')
            : path.join(projectPath, 'src');

        if (!fs.existsSync(basePath)) return utilities;

        // 搜索工具类目录
        const utilDirs = ['utils', 'util', 'helpers', 'lib', 'common', 'shared'];
        const fileExt = type === 'flutter' ? '**/*.dart' : '**/*.{ts,js}';

        for (const dir of utilDirs) {
            const dirPath = path.join(basePath, dir);
            if (!fs.existsSync(dirPath)) continue;

            const files = await glob(fileExt, { cwd: dirPath });
            
            for (const file of files) {
                const filePath = path.join(dirPath, file);
                const analysis = await this.analyzeUtilityFile(filePath, type);
                if (analysis) {
                    utilities.push(analysis);
                }
            }
        }

        // 分析 API 层
        const apiDirs = ['api', 'services', 'http'];
        for (const dir of apiDirs) {
            const dirPath = path.join(basePath, dir);
            if (!fs.existsSync(dirPath)) continue;

            const files = await glob(fileExt, { cwd: dirPath });
            
            for (const file of files) {
                const filePath = path.join(dirPath, file);
                const analysis = await this.analyzeUtilityFile(filePath, type);
                if (analysis) {
                    analysis.type = 'http';
                    utilities.push(analysis);
                }
            }
        }

        // 计算使用频率
        await this.calculateUsageCount(projectPath, utilities, type);

        // 标记推荐复用的工具类
        for (const util of utilities) {
            util.recommended = util.usageCount > 3 || 
                               util.type === 'http' || 
                               util.type === 'validation' ||
                               util.type === 'format';
        }

        return utilities;
    }

    /**
     * 分析单个工具类文件
     */
    private async analyzeUtilityFile(filePath: string, type: string): Promise<UtilityAnalysis | null> {
        try {
            const content = fs.readFileSync(filePath, 'utf-8');
            const relativePath = filePath;

            // 提取导出
            const exports: string[] = [];
            if (type === 'flutter') {
                // Dart 导出
                const classMatches = content.matchAll(/class\s+(\w+)/g);
                for (const match of classMatches) {
                    exports.push(match[1]);
                }
                const funcMatches = content.matchAll(/^\s*(\w+)\s*\([^)]*\)\s*{/gm);
                for (const match of funcMatches) {
                    if (!['if', 'for', 'while', 'switch', 'catch'].includes(match[1])) {
                        exports.push(match[1]);
                    }
                }
            } else {
                // TypeScript/JavaScript 导出
                const exportMatches = content.matchAll(/export\s+(?:const|function|class|interface|type)\s+(\w+)/g);
                for (const match of exportMatches) {
                    exports.push(match[1]);
                }
            }

            // 检测工具类类型
            let utilType: UtilityAnalysis['type'] = 'other';
            const lowerContent = content.toLowerCase();
            const fileName = path.basename(filePath).toLowerCase();

            if (lowerContent.includes('axios') || lowerContent.includes('fetch') || lowerContent.includes('http') || fileName.includes('request')) {
                utilType = 'http';
            } else if (lowerContent.includes('localstorage') || lowerContent.includes('sessionstorage') || fileName.includes('storage')) {
                utilType = 'storage';
            } else if (lowerContent.includes('validate') || lowerContent.includes('validator') || fileName.includes('valid')) {
                utilType = 'validation';
            } else if (lowerContent.includes('format') || lowerContent.includes('date') || fileName.includes('format')) {
                utilType = 'format';
            } else if (lowerContent.includes('token') || lowerContent.includes('auth') || fileName.includes('auth')) {
                utilType = 'auth';
            } else if (lowerContent.includes('router') || lowerContent.includes('route')) {
                utilType = 'router';
            } else if (lowerContent.includes('i18n') || lowerContent.includes('locale') || lowerContent.includes('translate')) {
                utilType = 'i18n';
            }

            // 提取依赖
            const dependencies: string[] = [];
            if (type === 'flutter') {
                const importMatches = content.matchAll(/import\s+'package:(\w+)/g);
                for (const match of importMatches) {
                    if (!dependencies.includes(match[1])) {
                        dependencies.push(match[1]);
                    }
                }
            } else {
                const importMatches = content.matchAll(/import\s+.*from\s+['"]([^'"]+)['"]/g);
                for (const match of importMatches) {
                    const dep = match[1];
                    if (!dep.startsWith('.') && !dep.startsWith('@/')) {
                        const pkgName = dep.startsWith('@') ? dep.split('/').slice(0, 2).join('/') : dep.split('/')[0];
                        if (!dependencies.includes(pkgName)) {
                            dependencies.push(pkgName);
                        }
                    }
                }
            }

            return {
                path: relativePath,
                type: utilType,
                exports,
                dependencies,
                usageCount: 0,
                recommended: false
            };

        } catch (error) {
            return null;
        }
    }

    /**
     * 计算工具类使用频率
     */
    private async calculateUsageCount(projectPath: string, utilities: UtilityAnalysis[], type: string): Promise<void> {
        const basePath = type === 'flutter'
            ? path.join(projectPath, 'lib')
            : path.join(projectPath, 'src');

        const fileExt = type === 'flutter' ? '**/*.dart' : '**/*.{ts,js,vue,tsx,jsx}';
        const files = await glob(fileExt, { cwd: basePath, ignore: ['node_modules/**'] });

        for (const file of files) {
            try {
                const content = fs.readFileSync(path.join(basePath, file), 'utf-8');
                
                for (const util of utilities) {
                    // 检查是否引用了该工具类
                    const utilName = path.basename(util.path, path.extname(util.path));
                    if (content.includes(utilName)) {
                        util.usageCount++;
                    }
                }
            } catch {
                // 忽略读取错误
            }
        }
    }

    /**
     * 分析样式系统
     */
    private async analyzeStyleSystem(projectPath: string, type: string): Promise<StyleSystemAnalysis> {
        const result: StyleSystemAnalysis = {
            globalStyles: [],
            preprocessor: null,
            cssFrameworks: [],
            uiLibrary: null,
            uiOverrides: [],
            themeFiles: [],
            cssVariables: [],
            mixins: []
        };

        if (type === 'flutter') {
            // Flutter 主题分析
            const themeFiles = await glob('**/theme/**/*.dart', { cwd: projectPath });
            result.themeFiles = themeFiles;
            return result;
        }

        const srcPath = path.join(projectPath, 'src');
        if (!fs.existsSync(srcPath)) return result;

        // 检测全局样式
        const stylePatterns = ['**/*.css', '**/*.scss', '**/*.sass', '**/*.less', '**/*.styl'];
        for (const pattern of stylePatterns) {
            const files = await glob(pattern, { cwd: srcPath, ignore: ['node_modules/**'] });
            
            for (const file of files) {
                const ext = path.extname(file).slice(1);
                
                // 检测预处理器
                if (ext === 'scss' || ext === 'sass') result.preprocessor = ext;
                else if (ext === 'less') result.preprocessor = 'less';
                else if (ext === 'styl') result.preprocessor = 'stylus';

                // 检测全局样式
                if (file.includes('global') || file.includes('base') || file.includes('main') || file.includes('app')) {
                    result.globalStyles.push(file);
                }

                // 检测主题/变量文件
                if (file.includes('variable') || file.includes('theme') || file.includes('token')) {
                    result.themeFiles.push(file);
                }

                // 检测 mixin
                if (file.includes('mixin')) {
                    result.mixins.push(file);
                }
            }
        }

        // 检测 UI 库覆盖样式
        const overridePatterns = ['**/element*.{css,scss,less}', '**/antd*.{css,scss,less}', '**/override*.{css,scss,less}'];
        for (const pattern of overridePatterns) {
            const files = await glob(pattern, { cwd: srcPath });
            if (files.length > 0) {
                result.uiOverrides.push({
                    path: files[0],
                    components: [] // 需要进一步分析
                });
            }
        }

        return result;
    }

    /**
     * 分析配置系统
     */
    private async analyzeConfigSystem(projectPath: string, type: string): Promise<ConfigSystemAnalysis> {
        const result: ConfigSystemAnalysis = {
            envFiles: [],
            buildConfig: null,
            routerConfig: null,
            stateConfig: null,
            httpConfig: null,
            i18nConfig: null,
            otherConfigs: []
        };

        // 环境文件
        const envFiles = await glob('.env*', { cwd: projectPath, dot: true });
        result.envFiles = envFiles;

        if (type === 'flutter') {
            // Flutter 配置
            return result;
        }

        // 构建配置
        if (fs.existsSync(path.join(projectPath, 'vite.config.ts'))) {
            result.buildConfig = {
                tool: 'vite',
                configFile: 'vite.config.ts',
                keyFeatures: []
            };
        } else if (fs.existsSync(path.join(projectPath, 'vue.config.js'))) {
            result.buildConfig = {
                tool: 'webpack',
                configFile: 'vue.config.js',
                keyFeatures: []
            };
        }

        // 路由配置
        const srcPath = path.join(projectPath, 'src');
        const routerFiles = await glob('**/router/**/*.{ts,js}', { cwd: srcPath });
        if (routerFiles.length > 0) {
            result.routerConfig = {
                type: 'vue-router',
                configFile: routerFiles[0],
                features: []
            };
        }

        // 状态管理配置
        const storeFiles = await glob('**/{stores,store}/**/*.{ts,js}', { cwd: srcPath });
        if (storeFiles.length > 0) {
            result.stateConfig = {
                type: 'pinia',
                configFile: storeFiles[0]
            };
        }

        // HTTP 配置
        const apiFiles = await glob('**/api/**/*.{ts,js}', { cwd: srcPath });
        if (apiFiles.length > 0) {
            result.httpConfig = {
                client: 'axios',
                configFile: apiFiles.find(f => f.includes('request') || f.includes('axios') || f.includes('http')) || apiFiles[0],
                features: []
            };
        }

        // 国际化配置
        const i18nFiles = await glob('**/{i18n,locales}/**/*.{ts,js,json}', { cwd: srcPath });
        if (i18nFiles.length > 0) {
            const locales = i18nFiles
                .filter(f => f.endsWith('.json'))
                .map(f => path.basename(f, '.json'));
            result.i18nConfig = {
                type: 'vue-i18n',
                configFile: i18nFiles.find(f => f.includes('index')) || i18nFiles[0],
                locales: [...new Set(locales)]
            };
        }

        return result;
    }

    /**
     * 分析核心页面
     */
    private async analyzeCorePages(projectPath: string, type: string): Promise<CorePagesAnalysis> {
        const result: CorePagesAnalysis = {
            loginPage: null,
            mainLayout: null,
            navigation: null,
            errorPages: [],
            otherCorePages: []
        };

        const basePath = type === 'flutter'
            ? path.join(projectPath, 'lib')
            : path.join(projectPath, 'src');

        if (!fs.existsSync(basePath)) return result;

        const fileExt = type === 'flutter' ? '**/*.dart' : '**/*.{vue,tsx,jsx}';
        const files = await glob(fileExt, { cwd: basePath });

        for (const file of files) {
            const fileName = file.toLowerCase();
            
            if (fileName.includes('login')) {
                result.loginPage = file;
            } else if (fileName.includes('layout') && (fileName.includes('main') || fileName.includes('default') || fileName.includes('app'))) {
                result.mainLayout = file;
            } else if (fileName.includes('sidebar') || fileName.includes('navbar') || fileName.includes('menu')) {
                result.navigation = file;
            } else if (fileName.includes('404') || fileName.includes('403') || fileName.includes('error') || fileName.includes('notfound')) {
                result.errorPages.push(file);
            }
        }

        return result;
    }

    /**
     * 生成项目骨架
     */
    private generateSkeleton(analysis: DeepAnalysisResult): ProjectSkeleton {
        const skeleton: ProjectSkeleton = {
            requiredDirectories: [],
            coreUtils: [],
            styleSystem: analysis.styleSystem,
            configSystem: analysis.configSystem,
            corePages: analysis.corePages,
            businessFilesToRemove: [],
            filesToClean: []
        };

        // 从架构层级中提取可复用的目录
        for (const layer of analysis.architectureLayers) {
            if (layer.reusable) {
                skeleton.requiredDirectories.push({
                    path: layer.directories[0],
                    description: layer.description,
                    files: layer.keyFiles
                });
            }
        }

        // 提取核心工具类
        skeleton.coreUtils = analysis.utilities.filter(u => u.recommended);

        // 标记业务文件（需要删除或清理）
        for (const layer of analysis.architectureLayers) {
            if (!layer.reusable && layer.name !== '视图层' && layer.name !== '页面层') {
                skeleton.filesToClean.push(...layer.directories.map(d => `src/${d}/**/*`));
            }
        }

        return skeleton;
    }

    /**
     * 评估项目质量
     */
    private assessQuality(analysis: DeepAnalysisResult): DeepAnalysisResult['qualityAssessment'] {
        const assessment = {
            score: 0,
            strengths: [] as string[],
            weaknesses: [] as string[],
            suggestions: [] as string[]
        };

        let score = 50;

        // 架构层级清晰度
        if (analysis.architectureLayers.length >= 5) {
            score += 10;
            assessment.strengths.push('项目架构层级清晰，模块划分合理');
        } else if (analysis.architectureLayers.length < 3) {
            score -= 10;
            assessment.weaknesses.push('项目结构较简单，缺少清晰的分层');
        }

        // 工具类复用度
        const reusableUtils = analysis.utilities.filter(u => u.recommended).length;
        if (reusableUtils >= 5) {
            score += 10;
            assessment.strengths.push('有丰富的可复用工具类');
        }

        // TypeScript 使用
        if (analysis.techStack.language === 'TypeScript') {
            score += 10;
            assessment.strengths.push('使用 TypeScript 提供类型安全');
        } else {
            assessment.suggestions.push('建议使用 TypeScript 提升代码质量');
        }

        // 样式系统
        if (analysis.styleSystem.themeFiles.length > 0) {
            score += 5;
            assessment.strengths.push('有完善的主题/变量系统');
        } else {
            assessment.suggestions.push('建议建立统一的主题变量系统');
        }

        // 配置系统
        if (analysis.configSystem.httpConfig) {
            score += 5;
            assessment.strengths.push('HTTP 请求已统一封装');
        }

        if (analysis.configSystem.i18nConfig) {
            score += 5;
            assessment.strengths.push('已配置国际化支持');
        }

        assessment.score = Math.min(100, Math.max(0, score));

        return assessment;
    }

    /**
     * 建议 MCP 模板
     */
    private suggestTemplates(analysis: DeepAnalysisResult): DeepAnalysisResult['templateSuggestions'] {
        const suggestions: DeepAnalysisResult['templateSuggestions'] = [];

        // 检查 HTTP 配置是否需要优化
        if (!analysis.configSystem.httpConfig) {
            suggestions.push({
                templateId: 'vue/api-layer',
                reason: '缺少统一的 HTTP 请求封装',
                replaceFiles: []
            });
        }

        return suggestions;
    }

    /**
     * 生成开发计划
     */
    generateDevelopmentPlan(analysis: DeepAnalysisResult, newProjectName: string): DevelopmentPlan {
        const plan: DevelopmentPlan = {
            title: `基于 ${analysis.projectName} 创建 ${newProjectName} 开发计划`,
            referenceProject: analysis.projectName,
            newProjectName,
            generatedAt: new Date().toISOString(),
            phases: [],
            notes: []
        };

        // 阶段 1: 项目初始化
        plan.phases.push({
            phase: 1,
            name: '项目初始化',
            description: '创建项目骨架，复制基础配置',
            tasks: [
                { task: '创建项目目录结构', priority: 'high', estimatedTime: '10min' },
                { task: '复制构建配置', priority: 'high', estimatedTime: '5min', files: [analysis.configSystem.buildConfig?.configFile || ''] },
                { task: '复制环境配置', priority: 'high', estimatedTime: '5min', files: analysis.configSystem.envFiles },
                { task: '安装依赖', priority: 'high', estimatedTime: '5min' }
            ]
        });

        // 阶段 2: 基础设施
        plan.phases.push({
            phase: 2,
            name: '基础设施搭建',
            description: '复制工具类、配置文件等基础代码',
            tasks: [
                { task: '复制工具类', priority: 'high', estimatedTime: '15min', files: analysis.skeleton.coreUtils.map(u => u.path) },
                { task: '复制 HTTP 配置', priority: 'high', estimatedTime: '10min', files: analysis.configSystem.httpConfig ? [analysis.configSystem.httpConfig.configFile] : [] },
                { task: '复制路由配置（骨架）', priority: 'medium', estimatedTime: '10min' },
                { task: '复制类型定义', priority: 'medium', estimatedTime: '5min' }
            ]
        });

        // 阶段 3: 样式系统
        plan.phases.push({
            phase: 3,
            name: '样式系统迁移',
            description: '复制全局样式、主题配置、UI 组件覆盖样式',
            tasks: [
                { task: '复制全局样式', priority: 'high', estimatedTime: '10min', files: analysis.styleSystem.globalStyles },
                { task: '复制主题/变量文件', priority: 'high', estimatedTime: '5min', files: analysis.styleSystem.themeFiles },
                { task: '复制 UI 组件覆盖样式', priority: 'medium', estimatedTime: '10min', files: analysis.styleSystem.uiOverrides.map(o => o.path) },
                { task: '复制 mixins', priority: 'low', estimatedTime: '5min', files: analysis.styleSystem.mixins }
            ]
        });

        // 阶段 4: 核心页面
        plan.phases.push({
            phase: 4,
            name: '核心页面搭建',
            description: '复制登录页、主布局、导航等核心页面',
            tasks: [
                { task: '复制主布局', priority: 'high', estimatedTime: '15min', files: analysis.corePages.mainLayout ? [analysis.corePages.mainLayout] : [] },
                { task: '复制登录页', priority: 'high', estimatedTime: '15min', files: analysis.corePages.loginPage ? [analysis.corePages.loginPage] : [] },
                { task: '复制导航组件', priority: 'medium', estimatedTime: '10min', files: analysis.corePages.navigation ? [analysis.corePages.navigation] : [] },
                { task: '复制错误页面', priority: 'low', estimatedTime: '10min', files: analysis.corePages.errorPages }
            ]
        });

        // 阶段 5: 清理和优化
        plan.phases.push({
            phase: 5,
            name: '清理和优化',
            description: '删除业务代码、优化配置',
            tasks: [
                { task: '清理业务组件', priority: 'high', estimatedTime: '20min' },
                { task: '清理业务视图', priority: 'high', estimatedTime: '20min' },
                { task: '清理业务 API', priority: 'medium', estimatedTime: '15min' },
                { task: '更新项目名称和配置', priority: 'high', estimatedTime: '10min' }
            ]
        });

        // 注意事项
        plan.notes = [
            `参考项目技术栈: ${analysis.techStack.framework} + ${analysis.techStack.language}`,
            `UI 库: ${analysis.techStack.uiLibrary || '无'}`,
            `状态管理: ${analysis.techStack.stateManagement || '无'}`,
            '确保保持与参考项目一致的代码风格',
            '注意检查硬编码的项目名称和配置',
            '建议先运行参考项目，确保理解其功能'
        ];

        return plan;
    }
}

/**
 * 获取默认分析器实例
 */
export function getProjectCloneAnalyzer(logger?: Logger): ProjectCloneAnalyzer {
    return new ProjectCloneAnalyzer(logger);
}
